<script setup>
import axios from "axios";
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const route   = useRoute();
const router  = useRouter();
const loading = ref(true);
const hasError = ref(false);
const results = ref([]);

const fetchResults = async () => {
  loading.value = true;
  hasError.value = false;
  results.value = [];
  try {
    const response = await axios.get(`/user-exam-results`);
    results.value = response.data.data || response.data || [];
  } catch (error) {
    console.error("خطأ في جلب النتايج:", error);
    hasError.value = true;
  } finally {
    loading.value = false;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "غير محدد";
  return new Date(dateString).toLocaleDateString('ar-EG', {
    year:'numeric', month:'long', day:'numeric'
  });
};

const goToExam = (examId, attempt) => router.push(`/my_exam/${examId}?attempt=${attempt}`);

// Fires during setup() (before any mount/transition), then again whenever
// the route changes back to this page — immune to out-in transition timing.
watch(
  () => route.name,
  (name) => { if (name === 'myExamsPage') fetchResults(); },
  { immediate: true }
);
</script>

<template>
  <div class="results-page">
    <div class="results-container">

      <!-- Page header -->
      <div class="results-header">
        <div class="rh-icon-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path d="M9 11l3 3L22 4"/>
            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
          </svg>
        </div>
        <h1>نتائج امتحاناتي</h1>
        <p>اضغط على أي نتيجة لمشاهدة التفاصيل والإجابات</p>
      </div>

      <!-- Loading skeleton -->
      <div v-if="loading" class="results-grid">
        <div v-for="n in 6" :key="n" class="result-skeleton">
          <div class="sk sk-score"></div>
          <div class="sk-body">
            <div class="sk sk-badge"></div>
            <div class="sk sk-title"></div>
            <div class="sk sk-line"></div>
            <div class="sk sk-line sk-line--short"></div>
          </div>
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="hasError" class="results-empty">
        <div class="re-illustration">
          <svg viewBox="0 0 80 80" fill="none">
            <circle cx="40" cy="40" r="40" fill="rgba(239,68,68,0.08)"/>
            <path d="M40 24v20M40 52v4" stroke="#EF4444" stroke-width="3" stroke-linecap="round"/>
            <circle cx="40" cy="40" r="28" stroke="#EF4444" stroke-width="2" fill="none"/>
          </svg>
        </div>
        <h2>تعذّر تحميل النتائج</h2>
        <p>تحقق من اتصالك بالإنترنت وحاول مرة أخرى</p>
        <button class="re-cta" style="border:none;cursor:pointer;" @click="fetchResults">
          🔄 إعادة المحاولة
        </button>
      </div>

      <!-- Empty state -->
      <div v-else-if="!results.length" class="results-empty">
        <div class="re-illustration">
          <svg viewBox="0 0 80 80" fill="none">
            <circle cx="40" cy="40" r="40" fill="rgba(91,95,239,0.08)"/>
            <path d="M25 52V28a3 3 0 013-3h24a3 3 0 013 3v24a3 3 0 01-3 3H28a3 3 0 01-3-3z" stroke="#5B5FEF" stroke-width="2" fill="rgba(91,95,239,0.12)"/>
            <path d="M33 36h14M33 41h10M33 46h8" stroke="#5B5FEF" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <h2>لم تؤدِّ أي امتحان بعد</h2>
        <p>ابدأ الآن وسيظهر سجل نتائجك هنا</p>
        <router-link to="/exams?type=online" class="re-cta">
          🚀 اذهب للامتحانات
        </router-link>
      </div>

      <!-- Results grid -->
      <div v-else class="results-grid">
        <div
          v-for="result in results"
          :key="result.id"
          class="result-card"
          :class="result.percentage >= 60 ? 'rc--pass' : 'rc--fail'"
          @click="goToExam(result.exam_id, result.attempt)"
        >
          <!-- Left: Score column -->
          <div class="rc-score-col" :class="result.percentage >= 60 ? 'sc--pass' : 'sc--fail'">
            <span class="rc-pct">{{ result.percentage }}%</span>
            <span class="rc-verdict">{{ result.percentage >= 60 ? 'ناجح' : 'راسب' }}</span>
            <div class="rc-score-nums">
              <strong>{{ result.student_score ?? result.total_score }}</strong>
              <span> / {{ result.exam_total_score ?? result.max_score }}</span>
            </div>
          </div>

          <!-- Right: Info column -->
          <div class="rc-info-col">
            <!-- Exam title -->
            <h3 class="rc-exam-title">{{ result.exam?.title || 'امتحان' }}</h3>

            <!-- Lesson tag -->
            <span class="rc-lesson-tag">
              <svg viewBox="0 0 16 16" fill="currentColor" class="rc-tag-icon"><path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 12.5A5.5 5.5 0 118 2.5a5.5 5.5 0 010 11zM7.25 5.5h1.5V9h-1.5zm0 4.5h1.5v1.5h-1.5z"/></svg>
              {{ result.exam?.lesson?.title || 'بدون درس' }}
            </span>

            <!-- Stats row -->
            <div class="rc-stats">
              <div class="rc-stat rc-stat--correct">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 8l3.5 3.5 7-7" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ result.correct_answers }} صحيح</span>
              </div>
              <div class="rc-stat-divider"></div>
              <div class="rc-stat rc-stat--wrong">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4l8 8M12 4l-8 8" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ (result.total_questions - result.correct_answers) }} خاطئ</span>
              </div>
              <div class="rc-stat-divider"></div>
              <div class="rc-stat rc-stat--date">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="2" y="3" width="12" height="11" rx="1.5"/><path d="M5 1v3M11 1v3M2 7h12"/>
                </svg>
                <span>{{ formatDate(result.submitted_at) }}</span>
              </div>
            </div>

            <!-- Tap hint -->
            <div class="rc-tap-hint">
              اضغط للتفاصيل
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" class="rc-arrow">
                <path d="M10 4l-4 4 4 4" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.results-page {
  min-height: calc(100vh - 70px);
  background: #F5F7FB;
  direction: rtl;
  font-family: 'Cairo', sans-serif;
  padding: 2rem 0 4rem;
}
.results-container { max-width: 1100px; margin: 0 auto; padding: 0 1.25rem; }

/* ── Header ── */
.results-header { text-align: center; padding: 2.25rem 1rem 2rem; }
.rh-icon-wrap {
  width: 68px; height: 68px;
  background: linear-gradient(135deg, #5B5FEF, #7C4DFF);
  border-radius: 20px;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 1rem;
  box-shadow: 0 14px 36px rgba(91,95,239,0.3);
}
.rh-icon-wrap svg { width: 32px; height: 32px; stroke: #fff; }
.results-header h1 {
  font-size: 2rem; font-weight: 800; margin-bottom: 0.4rem;
  background: linear-gradient(135deg, #5B5FEF, #7C4DFF);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}
.results-header p { color: #6B7280; font-size: 0.92rem; }

/* ── Grid ── */
.results-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)); gap: 1.25rem; }

/* ── Skeleton ── */
.result-skeleton {
  background: #fff; border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.06);
  overflow: hidden; display: flex;
}
.sk-score { width: 100px; height: 130px; flex-shrink: 0; border-radius: 0; }
.sk-body { flex: 1; padding: 1.25rem; display: flex; flex-direction: column; gap: 0.6rem; }
.sk {
  background: linear-gradient(90deg,#f0f0f0 25%,#e6e6e6 50%,#f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite; border-radius: 8px;
}
.sk-badge { height: 20px; width: 70px; border-radius: 99px; }
.sk-title { height: 18px; width: 80%; }
.sk-line  { height: 13px; width: 65%; }
.sk-line--short { width: 45%; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* ── Empty ── */
.results-empty { text-align: center; padding: 5rem 1rem; }
.re-illustration { width: 80px; height: 80px; margin: 0 auto 1.25rem; }
.re-illustration svg { width: 100%; height: 100%; }
.results-empty h2 { font-size: 1.4rem; font-weight: 800; color: #1A1A1A; margin-bottom: 0.4rem; }
.results-empty p  { font-size: 0.92rem; color: #6B7280; margin-bottom: 1.5rem; }
.re-cta {
  display: inline-flex; align-items: center; gap: 0.5rem;
  background: linear-gradient(135deg, #5B5FEF, #7C4DFF);
  color: #fff; padding: 0.8rem 2rem;
  border-radius: 99px; font-weight: 700; text-decoration: none;
  box-shadow: 0 12px 30px rgba(91,95,239,0.3); transition: all 0.3s;
}
.re-cta:hover { transform: translateY(-3px); color: #fff; box-shadow: 0 20px 45px rgba(91,95,239,0.45); }

/* ── Card ── */
.result-card {
  background: #fff; border-radius: 20px;
  box-shadow: 0 6px 24px rgba(0,0,0,0.07);
  cursor: pointer;
  transition: transform 0.3s cubic-bezier(.4,0,.2,1), box-shadow 0.3s;
  overflow: hidden;
  display: flex;
  border: 2px solid transparent;
}
.result-card:hover { transform: translateY(-6px); box-shadow: 0 20px 50px rgba(0,0,0,0.12); }
.rc--pass:hover { border-color: rgba(34,197,94,0.25); }
.rc--fail:hover { border-color: rgba(239,68,68,0.2); }

/* Score column */
.rc-score-col {
  width: 100px; flex-shrink: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 0.25rem; padding: 1.25rem 0.5rem;
  color: #fff; position: relative; overflow: hidden;
}
.rc-score-col::after {
  content: '';
  position: absolute; inset: 0;
  background: rgba(255,255,255,0.08);
  clip-path: ellipse(80% 50% at 110% 50%);
}
.sc--pass { background: linear-gradient(160deg, #22C55E, #16a34a); }
.sc--fail { background: linear-gradient(160deg, #EF4444, #dc2626); }

.rc-pct { font-size: 1.5rem; font-weight: 900; line-height: 1; }
.rc-verdict { font-size: 0.72rem; font-weight: 800; opacity: 0.9; }
.rc-score-nums { font-size: 0.76rem; opacity: 0.85; text-align: center; }
.rc-score-nums strong { font-size: 0.95rem; font-weight: 800; }

/* Info column */
.rc-info-col { flex: 1; padding: 1.1rem 1.25rem; display: flex; flex-direction: column; gap: 0.5rem; min-width: 0; }

.rc-exam-title {
  font-size: 1rem; font-weight: 800; color: #1A1A1A;
  line-height: 1.3;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.rc-lesson-tag {
  display: inline-flex; align-items: center; gap: 0.3rem;
  background: rgba(91,95,239,0.08); color: #5B5FEF;
  padding: 0.2rem 0.7rem; border-radius: 99px;
  font-size: 0.76rem; font-weight: 700;
  width: fit-content;
}
.rc-tag-icon { width: 12px; height: 12px; fill: #5B5FEF; }

/* Stats */
.rc-stats {
  display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap;
}
.rc-stat {
  display: flex; align-items: center; gap: 0.3rem;
  font-size: 0.78rem; font-weight: 600;
}
.rc-stat svg { width: 14px; height: 14px; flex-shrink: 0; }
.rc-stat--correct { color: #16a34a; }
.rc-stat--correct svg { stroke: #16a34a; }
.rc-stat--wrong   { color: #dc2626; }
.rc-stat--wrong   svg { stroke: #dc2626; }
.rc-stat--date    { color: #6B7280; }
.rc-stat--date    svg { stroke: #9CA3AF; }
.rc-stat-divider { width: 1px; height: 12px; background: #E5E7EB; }

/* Tap hint */
.rc-tap-hint {
  display: flex; align-items: center; gap: 0.3rem;
  font-size: 0.75rem; color: #9CA3AF; font-weight: 600;
  margin-top: auto; padding-top: 0.5rem;
  border-top: 1px solid rgba(91,95,239,0.07);
  transition: color 0.2s;
}
.rc-arrow { width: 14px; height: 14px; transition: transform 0.2s; }
.result-card:hover .rc-tap-hint { color: #5B5FEF; }
.result-card:hover .rc-arrow { transform: translateX(-3px); }

@media (max-width: 600px) {
  .results-header h1 { font-size: 1.6rem; }
  .results-grid { grid-template-columns: 1fr; }
  .rc-score-col { width: 86px; }
}
</style>
